import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useState, useEffect } from 'react'

// Define schema with flexible validation for form
const createMenuItemSchema = (t: (key: string) => string) => z.object({
  name: z.string().min(1, t('menuItems.form.nameRequired')),
  nameAr: z.string().optional(),
  description: z.string().optional(),
  descriptionAr: z.string().optional(),
  price: z.number().min(0, t('menuItems.form.priceInvalid')),
  isAvailable: z.boolean().optional().default(true),
  allergens: z.array(z.string()).optional().default([]),
  tags: z.array(z.string()).optional().default([]),
})

export interface MenuItem {
  id: string
  branchId?: string
  name: string
  nameAr?: string
  description?: string
  descriptionAr?: string
  price: number
  imageUrl?: string
  isAvailable: boolean
  allergens?: string[]
  tags?: string[]
  createdAt: string | Date
  updatedAt: string | Date
}

type MenuItemFormData = {
  name: string
  nameAr?: string
  description?: string
  descriptionAr?: string
  price: number
  isAvailable?: boolean
  allergens?: string[]
  tags?: string[]
}

interface MenuItemFormProps {
  menuItem?: MenuItem | null
  onSubmit: (data: MenuItemFormData) => void | Promise<void | MenuItem>
  onImageUpload?: (id: string, file: File) => Promise<string> // Returns imageUrl
  isLoading?: boolean
  error?: Error | null
  onCancel?: () => void
}

export default function MenuItemForm({
  menuItem,
  onSubmit,
  onImageUpload,
  isLoading = false,
  error,
  onCancel,
}: MenuItemFormProps) {
  const { t } = useTranslation('admin')
  
  // Create schema with translated error messages
  const menuItemSchema = createMenuItemSchema(t)

  const [imagePreview, setImagePreview] = useState<string | null>(
    menuItem?.imageUrl || null
  )
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imageUploadError, setImageUploadError] = useState<string | null>(null)
  
  // Update preview when menuItem changes (for edit mode)
  useEffect(() => {
    if (menuItem?.imageUrl && !imageFile) {
      setImagePreview(menuItem.imageUrl)
    }
  }, [menuItem?.imageUrl, imageFile])
  
  // Clear image upload error when image file changes
  useEffect(() => {
    if (imageFile) {
      setImageUploadError(null)
    }
  }, [imageFile])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<MenuItemFormData>({
    resolver: zodResolver(menuItemSchema),
    defaultValues: {
      name: menuItem?.name || '',
      nameAr: menuItem?.nameAr || '',
      description: menuItem?.description || '',
      descriptionAr: menuItem?.descriptionAr || '',
      price: menuItem?.price ? menuItem.price / 100 : 0, // Convert piasters to EGP for display
      isAvailable: menuItem?.isAvailable ?? true,
      allergens: menuItem?.allergens || [],
      tags: menuItem?.tags || [],
    },
  })

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleFormSubmit = async (data: MenuItemFormData) => {
    // Convert price from EGP to piasters
    const submitData: MenuItemFormData = {
      ...data,
      price: Math.round(data.price * 100),
    }
    
    // Submit form first (creates or updates the menu item)
    const result = await onSubmit(submitData)
    
    // Determine the menu item ID:
    // - For create: result should be the created item with id
    // - For edit: menuItem.id already exists
    const itemId = (result as MenuItem | undefined)?.id || menuItem?.id
    
    // If image file is selected and we have an upload handler, upload the image
    if (imageFile && onImageUpload && itemId) {
      try {
        setImageUploadError(null)
        await onImageUpload(itemId, imageFile)
        // Clear the image file state after successful upload
        setImageFile(null)
      } catch (err) {
        console.error('Error uploading image:', err)
        const errorMessage = err instanceof Error ? err.message : t('menuItems.form.imageUploadError')
        setImageUploadError(errorMessage)
        // Don't throw - item was created/updated successfully, just image upload failed
      }
    }
  }

  // Price watching disabled - not currently needed
  // Can be re-enabled for real-time price validation in the future
  // const _price = watch('price')

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="flex flex-col gap-[var(--spacing-md)]"
    >
      {error && (
        <Alert variant="destructive" data-testid="menu-item-form-error">
          <AlertDescription>
            {error.message || t('menuItems.form.errorOccurred')}
          </AlertDescription>
        </Alert>
      )}

      {imageUploadError && (
        <Alert variant="destructive" data-testid="menu-item-form-image-upload-error">
          <AlertDescription>
            {imageUploadError}
          </AlertDescription>
        </Alert>
      )}

      {/* Names - Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--spacing-md)]">
        {/* English Name */}
        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label htmlFor="name" data-testid="menu-item-form-name-label">
            {t('menuItems.form.name')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name"
            {...register('name')}
            placeholder={t('menuItems.form.namePlaceholder')}
            data-testid="menu-item-form-name-input"
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && (
            <p className="text-[0.8125rem] text-[var(--error)] mt-[var(--space-1)]">
              {errors.name.message}
            </p>
          )}
        </div>

        {/* Arabic Name */}
        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label htmlFor="nameAr" data-testid="menu-item-form-name-ar-label">
            {t('menuItems.form.nameAr')}
          </Label>
          <Input
            id="nameAr"
            {...register('nameAr')}
            placeholder={t('menuItems.form.nameArPlaceholder')}
            dir="rtl"
            data-testid="menu-item-form-name-ar-input"
            className={errors.nameAr ? 'border-destructive' : ''}
          />
        </div>
      </div>

      {/* Descriptions - Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--spacing-md)]">
        {/* English Description */}
        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label htmlFor="description">{t('menuItems.form.description')}</Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder={t('menuItems.form.descriptionPlaceholder')}
            data-testid="menu-item-form-description-input"
            rows={2}
            className="resize-none"
          />
        </div>

        {/* Arabic Description */}
        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label htmlFor="descriptionAr">{t('menuItems.form.descriptionAr')}</Label>
          <Textarea
            id="descriptionAr"
            {...register('descriptionAr')}
            placeholder={t('menuItems.form.descriptionArPlaceholder')}
            dir="rtl"
            data-testid="menu-item-form-description-ar-input"
            rows={2}
            className="resize-none"
          />
        </div>
      </div>

      {/* Price and Availability - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--spacing-md)]">
        {/* Price */}
        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label htmlFor="price">
            {t('menuItems.form.price')} <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Input
              id="price"
              type="number"
              step="0.01"
              {...register('price', { valueAsNumber: true })}
              placeholder={t('menuItems.form.pricePlaceholder')}
              data-testid="menu-item-form-price-input"
              className={`pr-12 rtl:pr-0 rtl:pl-12 ${errors.price ? 'border-destructive' : ''}`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 rtl:right-auto rtl:left-3 text-sm text-muted-foreground pointer-events-none">
              EGP
            </span>
          </div>
          {errors.price && (
            <p className="text-[0.8125rem] text-[var(--error)]">
              {errors.price.message}
            </p>
          )}
        </div>

        {/* Availability */}
        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label className="text-sm text-muted-foreground">{t('menuItems.form.status')}</Label>
          <div className="flex items-center gap-2 h-10">
            <input
              type="checkbox"
              id="isAvailable"
              {...register('isAvailable')}
              data-testid="menu-item-form-available-checkbox"
              className="h-4 w-4 rounded border border-input cursor-pointer"
            />
            <Label htmlFor="isAvailable" className="cursor-pointer font-normal">
              {t('menuItems.form.available')}
            </Label>
          </div>
        </div>
      </div>

      {/* Image Upload - Full Width */}
      <div className="flex flex-col gap-[var(--spacing-sm)]">
        <Label htmlFor="image">{t('menuItems.form.image')}</Label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
          {imagePreview ? (
            <div className="mb-3">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full max-w-xs h-32 object-cover rounded mx-auto"
              />
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-2">
                {t('menuItems.form.imageUpload')}
              </p>
            </div>
          )}
          <Input
            id="image"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            data-testid="menu-item-form-image-input"
            className="cursor-pointer"
          />
          <p className="text-xs text-muted-foreground mt-2 text-center">
            {t('menuItems.form.imageFormat')}
          </p>
        </div>
      </div>

      {/* Allergens and Tags - Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--spacing-md)]">
        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label htmlFor="allergens">{t('menuItems.form.allergens')}</Label>
          <Input
            id="allergens"
            placeholder={t('menuItems.form.allergensPlaceholder')}
            data-testid="menu-item-form-allergens-input"
            onChange={(e) => {
              const allergens = e.target.value.split(',').map(a => a.trim()).filter(Boolean)
              setValue('allergens', allergens)
            }}
            defaultValue={menuItem?.allergens?.join(', ') || ''}
          />
        </div>

        <div className="flex flex-col gap-[var(--spacing-sm)]">
          <Label htmlFor="tags">{t('menuItems.form.tags')}</Label>
          <Input
            id="tags"
            placeholder={t('menuItems.form.tagsPlaceholder')}
            data-testid="menu-item-form-tags-input"
            onChange={(e) => {
              const tags = e.target.value.split(',').map(t => t.trim()).filter(Boolean)
              setValue('tags', tags)
            }}
            defaultValue={menuItem?.tags?.join(', ') || ''}
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex gap-2 rtl:flex-row-reverse pt-2">
        <Button
          type="submit"
          disabled={isSubmitting || isLoading}
          data-testid="menu-item-form-submit"
        >
          {menuItem ? t('menuItems.form.updateItem') : t('menuItems.form.createItem')}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting || isLoading}
            data-testid="menu-item-form-cancel"
          >
            {t('menuItems.form.cancel')}
          </Button>
        )}
      </div>
    </form>
  )
}
